import { BaseApiService, ApiResponse } from './baseApi';
import { API_ENDPOINTS, HTTP_METHODS } from '../config/api';
import { toast } from 'sonner';

export interface Branch {
  id: number;
  branchName: string;
  branchCode: string;
  address: string;
  contactPerson: string;
  phone: string;
  email: string;
  isActive: boolean;
  gstin: string;
  stateName: string;
  stateCode: string;
  panNo: string;
  createdAt: string;
  updatedAt: string;
}

class BranchApiService extends BaseApiService {

  // Get all branches
  async getBranches(): Promise<Branch[]> {
    try {
      const response = await this.makeRequest<ApiResponse<Branch[]>>(API_ENDPOINTS.BRANCHES.LIST);
      return response.data;
    } catch (error) {
      // console.error('BranchApiService: Error fetching branches:', error);
      toast.error('Failed to fetch branches');
      throw error;
    }
  }

  // Get branch by ID
  async getBranchById(id: string): Promise<Branch> {
    try {
      const response = await this.makeRequest<ApiResponse<Branch>>(API_ENDPOINTS.BRANCHES.DETAIL(id));
      return response.data;
    } catch (error) {
      toast.error('Failed to fetch branch details');
      throw error;
    }
  }

  // Create new branch
  async createBranch(branchData: Omit<Branch, 'id'>): Promise<Branch> {
    try {
      const response = await this.makeRequest<ApiResponse<Branch>>(API_ENDPOINTS.BRANCHES.CREATE, {
        method: HTTP_METHODS.POST,
        body: JSON.stringify(branchData),
      });
      if (response.success) {
        toast.success(response.message || 'Branch created successfully!');
        return response.data;
      }
      throw new Error(response.message || 'Failed to create branch');
    } catch (error) {
      toast.error('Failed to create branch');
      throw error;
    }
  }

  // Update branch
  async updateBranch(id: string, branchData: Partial<Branch>): Promise<Branch> {
    try {
      const response = await this.makeRequest<ApiResponse<Branch>>(API_ENDPOINTS.BRANCHES.UPDATE(id), {
        method: HTTP_METHODS.PUT,
        body: JSON.stringify(branchData),
      });
      if (response.success) {
        toast.success(response.message || 'Branch updated successfully!');
        return response.data;
      }
      throw new Error(response.message || 'Failed to update branch');
    } catch (error) {
      toast.error('Failed to update branch');
      throw error;
    }
  }

  // Delete branch
  async deleteBranch(id: string): Promise<void> {
    try {
      const response = await this.makeRequest<ApiResponse<unknown>>(API_ENDPOINTS.BRANCHES.DELETE(id), {
        method: HTTP_METHODS.DELETE,
      });
      if (response?.success) {
        toast.success(response.message || 'Branch deleted successfully!');
        return;
      }
      throw new Error(response?.message || 'Failed to delete branch');
    } catch (error) {
      toast.error('Failed to delete branch');
      throw error;
    }
  }
}

export const branchApiService = new BranchApiService(); 